import { Product } from '@axo/shared/data-access/types';
import { Subject } from '@axo/shared/util/reactivity';
import debounce from 'lodash/debounce';

export interface queueOptions {
  immediate?: boolean;
}

type Flatten<TData> = TData[keyof TData];
type Data<TData> = Flatten<{
  [K in keyof TData]: { [_ in K | `${K & string}/${string}`]: TData[K] };
}>;

export type HandlerResult<TPreconditions> = {
  didSync: boolean;
  newPreconditions?: Partial<TPreconditions>;
};
export type Handler<
  TData,
  TPreconditions = Record<string, unknown>
> = (context: {
  data: TData;
  storedData: Partial<TData>;
  preconditions: Readonly<TPreconditions>;
  baseUrl: string;
  frontendId: string;
}) => Promise<HandlerResult<TPreconditions>> | HandlerResult<TPreconditions>;

export interface Dependencies<TData, TPreconditions> {
  handlers: {
    [K in keyof TData]: Handler<TData[K], TPreconditions>;
  };
  product?: Product;
  baseUrl: string;
  initialPreconditions: TPreconditions;
  onPreconditionChange?: (
    preconditions: TPreconditions,
    product?: Product
  ) => void;
}

export const apiOrchestratorFactory = <
  TData extends Record<string, unknown>,
  TPreconditions
>({
  product,
  handlers,
  baseUrl,
  initialPreconditions,
  onPreconditionChange,
}: Dependencies<TData, TPreconditions>) => {
  let queued: Partial<Record<string, TData[keyof TData]>> = {};
  const sent: Record<string, TData[keyof TData]> = {};
  const loadingStates: Record<string, Promise<unknown>[]> = {};
  let preconditions: TPreconditions = initialPreconditions;

  const externalState = new Subject<{ isLoading: boolean }>({
    isLoading: false,
  });

  const runAfterAllPreviousCallsGroupedByKey = (
    queueKey: string,
    fn: () => Promise<unknown>
  ) => {
    if (queueKey.includes('Debt/')) {
      queueKey = 'Debt';
    }
    if (queueKey.includes('Child/')) {
      queueKey = 'Child';
    }
    if (queueKey.includes('Expense/')) {
      queueKey = 'Expense';
    }

    const promise = Promise.all(loadingStates[queueKey] ?? []).then(fn);

    loadingStates[queueKey] = [...(loadingStates[queueKey] ?? []), promise];
    externalState.update({
      isLoading: Object.values(loadingStates).flat().length > 0,
    });

    promise.finally(() => {
      loadingStates[queueKey] = loadingStates[queueKey].filter(
        (p) => p !== promise
      );
      externalState.update({
        isLoading: Object.values(loadingStates).flat().length > 0,
      });
    });

    return promise;
  };

  const tryToSendQueuedData = (): Promise<unknown> => {
    const handlerCalls = getHandlerCallsForQueuedChanges(handlers, queued);

    const promises: Promise<unknown>[] = handlerCalls.map(({ handler, key }) =>
      runAfterAllPreviousCallsGroupedByKey(key, async () => {
        const value = queued[key];
        if (equals(sent[key], value)) return;
        if (!value) return;

        const result = await Promise.resolve(
          handler({
            data: value,
            storedData: sent[key] ?? {},
            preconditions: preconditions,
            baseUrl,
            frontendId: key,
          })
        );

        if (result.didSync) {
          sent[key] = structuredClone(value);
        }
        if (
          result?.newPreconditions &&
          !equals(result.newPreconditions, preconditions)
        ) {
          preconditions = { ...preconditions, ...result.newPreconditions };
          onPreconditionChange?.(preconditions, product);
          tryToSendQueuedData();
        }
      })
    );

    return Promise.all(promises);
  };

  const setPreconditions = (newPreconditions: Partial<TPreconditions>) => {
    preconditions = { ...preconditions, ...newPreconditions };
  };

  const debouncedQueue = debounce((changes: Partial<Data<TData>>) => {
    queued = changes;
    return tryToSendQueuedData();
  }, 1000);

  const queue = (changes: Partial<Data<TData>>, options?: queueOptions) => {
    if (options?.immediate) {
      debouncedQueue(changes);
      return debouncedQueue.flush();
    }
    return debouncedQueue(changes);
  };

  return {
    state: externalState.readonly,
    queue,
    setPreconditions,
    getPreconditions: () => preconditions,
  };
};

const getHandlerCallsForQueuedChanges = <TData, TPreconditions>(
  handlers: Dependencies<TData, TPreconditions>['handlers'],
  queued: Partial<Record<string, TData[keyof TData]>>
) => {
  return entries(handlers).flatMap(([handlerType, handler]) => {
    const keyAllowListRegex = new RegExp(`^${handlerType}(/.+)?`);

    return Object.keys(queued)
      .filter((key) => keyAllowListRegex.test(key))
      .map((key) => ({
        handler,
        key,
      }));
  });
};

const entries = <TData extends Record<string, unknown>>(obj: TData) =>
  Object.entries(obj) as {
    [K in keyof TData]: [K & string, TData[K]];
  }[keyof TData][];

const equals = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);
